import {
  Contact,
  File,
  MailConfiguration,
  Period,
  Signatory,
  Signature,
} from "@type/core/dashboard/dashboard.types";
import { SortDetails, SpecificField } from "@type/core/globalTypes";
import { ModelMail } from "@type/core/models/models.types";

export interface ExtraRecipient {
  key: string;
  firstname: string;
  lastname: string;
  email: string;
}

export type TransactionSignatory = Pick<
  Signatory,
  "certificate" | "email" | "firstname" | "lastname" | "phone"
>;

export type TransactionStatus =
  | "created"
  | "draft"
  | "prepared"
  | "inProgress"
  | "finished"
  | "classified"
  | "refused"
  | "canceled"
  | "expired"
  | "error"
  | "deleted"
  | "pending"
  | "processing";

export interface Transaction {
  id?: string;
  type?: string;
  signatories?: TransactionSignatory[];
  invitationMailConfiguration?: MailConfiguration;
  endOfTransactionMailConfiguration?: MailConfiguration;
  reference?: string;
  status?: TransactionStatus;
  office?: string;
  relaunch?: boolean;
  cause?: string;
  confidential?: boolean;
  notification?: TransactionNotification;
  extraRecipients?: ExtraRecipient[];
  authorizedExpertForConfidentiality?: Contact[];
  locked?: number;
  invitationMode?: InvitationMode;
  modelTransactionId?: string;
  endOfTransactionModelMailId?: string;
  originEditor?: boolean;
}

export interface TransactionDetail {
  id: string;
  object?: string;
  emittedDate?: string;
  expirationDate?: string;
  lastModificationDate?: string;
  status?: TransactionStatus;
  type?: TransactionType;
  officeId?: string;
  officeName?: string;
  progression?: string;
  signatories?: Signatory[];
  sendBy?: string;
  classified?: boolean;
  tsError?: boolean;
  cause?: string;
  confidential?: boolean;
  authorized?: boolean;
  potentialCreditExhausted?: boolean;
  manualPositioning?: boolean;
  direct?: boolean;
  directDate?: string;
  canBeDuplicated?: boolean;
  hasDataOrigin?: boolean;
  hasData?: boolean;
  notification?: TransactionNotification;
  extraRecipients?: ExtraRecipient[];
  numberOfLockedDocuments?: number;
  sealMoved?: boolean;
  invitationMailConfiguration?: MailConfiguration;
  endOfTransactionMailConfiguration?: MailConfiguration;
  isStoppedTransaction?: boolean;
  creationUser?: string;
  invitationMode?: InvitationMode;
  isInGroup?: boolean;
  originEditor?: boolean;
  signatureRequirementMode?: SignatureRequirementMode;
  universignVersion?: transactionUniversignVersion;
  message?: string;
  manuallyFinalized?: boolean;
  url?: string;
}

export interface UploadTransactionFilesResponse {
  ok?: boolean;
  status?: number;
  code?: string;
  details?: string;
}

// eslint-disable-next-line @typescript-eslint/naming-convention
type transactionUniversignVersion = "U1" | "U2";

type TransactionNotification =
  | "ALL"
  | "NONE"
  | "STATUS"
  | "SIGNATORIES"
  | "DISABLED";

export type TransactionForm = Pick<
  Transaction,
  | "office" // officeId for Model
  | "confidential" // isConfidential for Model
  | "authorizedExpertForConfidentiality"
  | "extraRecipients"
  | "reference"
  | "invitationMailConfiguration"
  | "endOfTransactionMailConfiguration"
  | "invitationMode"
  | "modelTransactionId"
  | "endOfTransactionModelMailId"
  | "id"
> & {
  invitation?: ModelMail;
  endOfTransaction?: ModelMail;
} & {
  // Add property unique to model
  name?: string;
};

export type TransactionUpdateField =
  | SpecificField<TransactionForm, "office">
  | SpecificField<TransactionForm, "confidential">
  | SpecificField<TransactionForm, "authorizedExpertForConfidentiality">
  | SpecificField<TransactionForm, "extraRecipients">
  | SpecificField<TransactionForm, "reference">
  | SpecificField<TransactionForm, "modelTransactionId">
  | SpecificField<TransactionForm, "endOfTransactionModelMailId">
  | SpecificField<TransactionForm, "invitation">
  | SpecificField<TransactionForm, "endOfTransaction">
  | SpecificField<TransactionForm, "invitationMode">
  | SpecificField<TransactionForm, "name"> // Unique to model
  | SpecificField<TransactionForm, "invitationMailConfiguration">
  | SpecificField<TransactionForm, "endOfTransactionMailConfiguration">
  | SpecificField<TransactionForm, "id">;

export type TransactionType = "signature" | "scellement";
export type InvitationMode = "parallel" | "sequential" | null;

export type SignatureRequirementMode = "ALL" | "SOME";

export interface CurrentTransactionHandler {
  transactionForm: TransactionForm;
  transactionType: TransactionType;
  transactionDetail?: TransactionDetail;
  attachments: File[];
  files: File[];
  signatories: Signatory[];
  signatures: Signature[];
  isSignaturesFromModel?: boolean;
  isFromDraft?: boolean;
  isEditing?: boolean;
  signatureRequirementMode: SignatureRequirementMode;
}

export interface ActivateSettings {
  activateAttachment: boolean;
  activateSignLevel2: boolean;
  displayMessage: boolean;
  errorMessage: string;
  errorLink: string;
  labelLink: string;
  certificateDegradedMode?: boolean;
  signatureDegradedMode?: boolean;
  sealDegradedMode?: boolean;
  ceDegradedMode?: boolean;
  shouldDisplayMigrationLink?: boolean;
  messageType: string;
  dashboardDegradedMode?: boolean;
  dashboardOpenIsActive?: boolean;
  hideU1OnlyPreferences: boolean;
  signaturePreviewVersion: number;
  onboardingIsBlocked: boolean;
}

export enum SignatureLevelAvailable {
  LEVEL_1 = "level_1",
  LEVEL_2 = "level_2",
  LEVEL_3 = "level_3",
}

export type TiersSignatureLevelAvailable = Extract<
  SignatureLevelAvailable,
  SignatureLevelAvailable.LEVEL_1 | SignatureLevelAvailable.LEVEL_2
>;
export type QcpSignatureLevelAvailable = Extract<
  SignatureLevelAvailable,
  SignatureLevelAvailable.LEVEL_2 | SignatureLevelAvailable.LEVEL_3
>;

export type MultipleTransactionAction = "launch" | "relaunch" | "classify";

export interface FinalizationRequest {
  reason: string;
}

export interface TransactionSearch {
  shortcut?: string;
  size?: number;
  types?: string[];
  offices?: string[];
  status?: string[];
  q?: string;
  page?: number;
  sort?: SortDetails;
  isHideConfidential?: boolean;
  lastModificationPeriod: Period | null;
  expirationPeriod: Period | null;
}
